import {
  Answers,
  BasketDTO,
  BasketQuestionDTO,
  BasketQuestionOptionDTO,
  ConditionRule,
  FlowTab,
  ModelChoices,
} from './types';

/** Sem `visibility` a pergunta está sempre visível. Aceita all/any com eq/neq/in. */
export const isVisible = (
  question: BasketQuestionDTO,
  answers: Answers
): boolean => {
  const matches = (rule: ConditionRule) => {
    const answer = answers[rule.question];
    if (rule.operator === 'in')
      return ([] as string[]).concat(rule.value).includes(answer);
    if (rule.operator === 'neq')
      return answer !== undefined && answer !== rule.value;
    return answer === rule.value;
  };

  const { visibility } = question;
  if (!visibility) return true;
  if (visibility.all && !visibility.all.every(matches)) return false;
  if (
    visibility.any &&
    !visibility.any.some((c) =>
      'all' in c ? c.all.every(matches) : matches(c)
    )
  )
    return false;
  return true;
};

/** Balaio da alternativa respondida, se ela tiver um. */
export const getAnsweredBasket = (
  question: BasketQuestionDTO,
  answers: Answers
): BasketDTO | undefined =>
  question.options.find((option) => option.value === answers[question.code])
    ?.action?.basket;

/** Balaio com mais de um produto: o usuário escolhe um deles no pop-up. */
const needsChoice = (basket: BasketDTO) => basket.products.length > 1;

/**
 * Balaio para "Especificar modelo": tem `allow_model_selector` e mais de um
 * produto. O pop-up abre ao clicar na alternativa.
 */
export const modelChoiceOf = (
  option: BasketQuestionOptionDTO
): BasketDTO | undefined => {
  const basket = option.action?.basket;
  return basket?.allow_model_selector && needsChoice(basket)
    ? basket
    : undefined;
};

/**
 * Balaio em que o usuário escolhe um dos produtos num select: mais de um
 * produto e sem `allow_model_selector`.
 */
export const selectChoiceOf = (
  option: BasketQuestionOptionDTO
): BasketDTO | undefined => {
  const basket = option.action?.basket;
  return basket && needsChoice(basket) && !basket.allow_model_selector
    ? basket
    : undefined;
};

/** Respondida de verdade: com resposta e, se pede escolha, com o produto escolhido. */
export const isAnswered = (
  question: BasketQuestionDTO,
  answers: Answers,
  models: ModelChoices
): boolean => {
  const basket = getAnsweredBasket(question, answers);
  if (!answers[question.code]) return false;
  return !basket || !needsChoice(basket) || models[question.code] !== undefined;
};

export type SelectedItem = {
  key: string;
  /** Pergunta que adicionou o produto (liga o produto ao seu bloco). */
  questionCode: string;
  name: string;
  detail: string;
  sapCode: string;
  quantity?: number;
};

/**
 * Uma linha por produto dos balaios escolhidos, só das perguntas visíveis —
 * respostas de perguntas que sumiram ficam no estado, mas não entram aqui.
 * Balaio com escolha de modelo adiciona só o modelo escolhido — sem escolha,
 * nenhum produto entra.
 */
export const getSelectedItems = (
  visible: BasketQuestionDTO[],
  answers: Answers,
  models: ModelChoices
): SelectedItem[] =>
  visible.flatMap((question) => {
    const basket = getAnsweredBasket(question, answers);
    if (!basket) return [];

    if (needsChoice(basket)) {
      const model = basket.products.find(
        (item) => item.id === models[question.code]
      );
      return model
        ? [
            {
              key: question.code,
              questionCode: question.code,
              name: model.description,
              detail: model.sap_code,
              sapCode: model.sap_code,
            },
          ]
        : [];
    }

    return basket.products.map((item) => ({
      key: `${question.code}-${item.id}`,
      questionCode: question.code,
      name: item.description,
      detail: item.sap_code,
      sapCode: item.sap_code,
    }));
  });

/** Perguntas das quais a visibilidade desta depende (all e any). */
const parentsOf = (question: BasketQuestionDTO) =>
  [
    ...(question.visibility?.all ?? []),
    ...(question.visibility?.any ?? []).flatMap((group) =>
      'all' in group ? group.all : [group]
    ),
  ].map((rule) => rule.question);

/**
 * Aba de cada pergunta. Sem condição: obrigatória → principal, opcional →
 * complementar. Com condição: a mesma aba da pergunta da qual depende, para
 * a pergunta aparecer junto de quem a abriu (ex.: o "Ciente" do backup).
 */
export const tabOf = (questions: BasketQuestionDTO[]) => {
  const byCode = new Map(questions.map((q) => [q.code, q]));
  const tabs = new Map<string, FlowTab>();

  const resolve = (question: BasketQuestionDTO, seen: Set<string>): FlowTab => {
    const known = tabs.get(question.code);
    if (known) return known;

    const parent = parentsOf(question)
      .map((code) => byCode.get(code))
      .find(
        (item): item is BasketQuestionDTO =>
          item !== undefined && !seen.has(item.code)
      );

    const tab: FlowTab = parent
      ? resolve(parent, new Set(seen).add(question.code))
      : question.required
      ? 'principal'
      : 'complementar';
    tabs.set(question.code, tab);
    return tab;
  };

  questions.forEach((question) => resolve(question, new Set()));
  return tabs;
};

/**
 * Primeira obrigatória visível sem resposta, olhando a aba principal antes da
 * complementar. É para onde o "Prosseguir" leva o usuário.
 */
export const firstMissing = (
  visible: BasketQuestionDTO[],
  answers: Answers,
  tabs: Map<string, FlowTab>,
  models: ModelChoices = {}
): BasketQuestionDTO | undefined => {
  const missing = visible.filter(
    (q) => q.required && !isAnswered(q, answers, models)
  );
  return (
    missing.find((q) => tabs.get(q.code) === 'principal') ??
    missing.find((q) => tabs.get(q.code) === 'complementar')
  );
};

/**
 * Código SAP do produto mostrado ao lado das alternativas: o modelo escolhido
 * no pop-up ou, em balaio sem escolha de modelo, o primeiro produto.
 */
export const answeredProductSap = (
  question: BasketQuestionDTO,
  answers: Answers,
  models: ModelChoices
): string | undefined => {
  const basket = getAnsweredBasket(question, answers);
  if (!basket) return undefined;
  if (needsChoice(basket)) {
    return basket.products.find((p) => p.id === models[question.code])
      ?.sap_code;
  }
  return basket.products[0]?.sap_code;
};

/**
 * Respostas no formato que a tela de finalizar (e saveOrder) já leem:
 * `configuredEquipment` por pergunta, com os códigos SAP (número) dos produtos
 * que ela adicionou, e `fullListProd` com os dados desses produtos por código.
 */
export const toOrderData = (
  visible: BasketQuestionDTO[],
  answers: Answers,
  models: ModelChoices
) => {
  const configuredEquipment: Record<
    string,
    { value: string; productsToAdd: number[] }
  > = {};
  const fullListProd: Record<
    number,
    { sap_code: string; description: string }
  > = {};

  visible.forEach((question) => {
    const value = answers[question.code];
    if (!value) return;
    const items = getSelectedItems([question], answers, models);
    configuredEquipment[question.code] = {
      value,
      productsToAdd: items.map((item) => Number(item.sapCode)),
    };
    items.forEach((item) => {
      fullListProd[Number(item.sapCode)] = {
        sap_code: item.sapCode,
        description: item.name,
      };
    });
  });

  return { configuredEquipment, fullListProd };
};

/**
 * Agrupa as perguntas visíveis em blocos: uma pergunta sem condição abre o
 * bloco e as que dependem dela (direta ou indiretamente) entram nele, na
 * ordem em que aparecem. Ex.: "Defina seu paciente" + "Tipo de Terapia".
 */
export const groupBlocks = (
  visible: BasketQuestionDTO[],
  all: BasketQuestionDTO[]
): BasketQuestionDTO[][] => {
  const byCode = new Map(all.map((q) => [q.code, q]));

  const rootOf = (question: BasketQuestionDTO, seen: Set<string>): string => {
    const parent = parentsOf(question)
      .map((code) => byCode.get(code))
      .find(
        (item): item is BasketQuestionDTO =>
          item !== undefined && !seen.has(item.code)
      );
    return parent
      ? rootOf(parent, new Set(seen).add(question.code))
      : question.code;
  };

  const groups = new Map<string, BasketQuestionDTO[]>();
  visible.forEach((question) => {
    const root = rootOf(question, new Set());
    groups.set(root, [...(groups.get(root) ?? []), question]);
  });
  return Array.from(groups.values());
};

export type BlockNode = { question: BasketQuestionDTO; children: BlockNode[] };

/**
 * Árvore de um bloco: a mãe de cada pergunta é a primeira pergunta da qual ela
 * depende que aparece antes dela no bloco; sem mãe, é raiz. Mostra quem abriu quem.
 */
export const blockTree = (group: BasketQuestionDTO[]): BlockNode[] => {
  const nodes = new Map<string, BlockNode>(
    group.map((question) => [question.code, { question, children: [] }])
  );
  const position = new Map(
    group.map((question, index) => [question.code, index])
  );
  const roots: BlockNode[] = [];

  group.forEach((question, index) => {
    const node = nodes.get(question.code);
    if (!node) return;
    const parentCode = parentsOf(question).find(
      (code) => (position.get(code) ?? Infinity) < index
    );
    const parent = parentCode ? nodes.get(parentCode) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  });
  return roots;
};
